/**
 * GESTOS DE DOS DEDOS sobre una capa.
 *
 * El lienzo ya bloquea los gestos del navegador con `touch-action: none`, pero
 * no los aprovechaba: en el movil solo se podia arrastrar. Pinza para escalar y
 * giro con dos dedos son la forma NATURAL de colocar algo en una pantalla
 * tactil, y sin ellos hay que ir al panel a escribir numeros con el pulgar.
 *
 * Aritmetica PURA: entra el estado de los dos dedos y sale la transformacion
 * que hay que aplicar. Ni DOM, ni Fabric, ni eventos. Estos calculos fallan de
 * formas dificiles de reproducir a mano —un giro que salta 360 grados, una
 * escala que se dispara al levantar un dedo— y probarlos con numeros es la
 * unica manera de fiarse de ellos.
 */

export interface Dedo {
  clientX: number;
  clientY: number;
}

export interface EstadoPinza {
  /** Separacion entre los dedos al empezar, en pixeles. */
  distancia: number;
  /** Angulo entre los dedos al empezar, en grados. */
  angulo: number;
  /** Punto medio al empezar. */
  centro: { x: number; y: number };
}

export interface Transformacion {
  /** Cuanto hay que multiplicar la escala. */
  escala: number;
  /** Cuanto hay que sumar al giro, en grados. */
  giroGrados: number;
  /** Cuanto se ha movido el centro, en pixeles. */
  dx: number;
  dy: number;
}

/** Escala minima y maxima de un gesto. Sin limites, un pellizco brusco borra la capa de la vista. */
export const ESCALA_MINIMA = 0.1;
export const ESCALA_MAXIMA = 20;

export function distanciaEntre(a: Dedo, b: Dedo): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

export function anguloEntre(a: Dedo, b: Dedo): number {
  return (Math.atan2(b.clientY - a.clientY, b.clientX - a.clientX) * 180) / Math.PI;
}

export function centroEntre(a: Dedo, b: Dedo): { x: number; y: number } {
  return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
}

/** El estado de partida del gesto, que se guarda al posar el segundo dedo. */
export function empezarPinza(a: Dedo, b: Dedo): EstadoPinza {
  return { distancia: distanciaEntre(a, b), angulo: anguloEntre(a, b), centro: centroEntre(a, b) };
}

/**
 * Normaliza un giro al intervalo (-180, 180].
 *
 * Sin esto, pasar de 179 a -179 grados —un movimiento de dos grados— se leeria
 * como un giro de 358 y la capa daria una vuelta completa de golpe.
 */
export function normalizarGiro(grados: number): number {
  let g = grados % 360;
  if (g > 180) g -= 360;
  if (g <= -180) g += 360;
  return g;
}

/**
 * Lo que ha cambiado desde que empezo el gesto.
 *
 * Se calcula contra el ESTADO INICIAL y no contra el cuadro anterior: acumular
 * incrementos arrastra el error de redondeo, y en un gesto de dos segundos a
 * sesenta cuadros por segundo eso se nota como una deriva.
 */
export function seguirPinza(inicio: EstadoPinza, a: Dedo, b: Dedo): Transformacion {
  const distancia = distanciaEntre(a, b);
  const centro = centroEntre(a, b);

  const escala =
    inicio.distancia > 0
      ? Math.min(ESCALA_MAXIMA, Math.max(ESCALA_MINIMA, distancia / inicio.distancia))
      : 1;

  return {
    escala,
    giroGrados: normalizarGiro(anguloEntre(a, b) - inicio.angulo),
    dx: centro.x - inicio.centro.x,
    dy: centro.y - inicio.centro.y,
  };
}

/**
 * Si el gesto es lo bastante grande como para hacerle caso.
 *
 * Al posar dos dedos siempre hay unos pixeles de temblor. Sin un umbral, una
 * capa se escala y se gira un poco cada vez que alguien la toca con dos dedos,
 * aunque solo quisiera moverla.
 */
export function esGestoIntencionado(t: Transformacion): boolean {
  return Math.abs(t.escala - 1) > 0.03 || Math.abs(t.giroGrados) > 2 || Math.hypot(t.dx, t.dy) > 4;
}

/**
 * Redondea el giro a los angulos de siempre cuando anda cerca.
 *
 * Nadie quiere un logotipo a 44,7 grados: quien gira a mano busca el recto, el
 * medio o el cuarto. Con quince grados de margen, apuntar a ojo basta.
 */
export function imantarGiro(grados: number, margen = 5): number {
  const notables = [-180, -135, -90, -45, 0, 45, 90, 135, 180];

  /*
    El MAS CERCANO, no el primero que entre en el margen.

    Con el margen de cinco grados nunca hay dos candidatos y da igual, pero con
    uno mas ancho si: un giro de 30 grados esta a 30 del recto y a 15 del
    cuarto, y quedarse con el primero de la lista lo mandaria al recto.
  */
  let mejor: number | null = null;
  let menorDistancia = Infinity;
  for (const n of notables) {
    const distancia = Math.abs(normalizarGiro(grados - n));
    if (distancia <= margen && distancia < menorDistancia) {
      menorDistancia = distancia;
      mejor = n;
    }
  }
  return mejor ?? grados;
}
